import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';
import { useToast } from '../../hooks/useToast';
import { authService } from '../../services/auth.service';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface KPIs {
  activeProjects: number;
  completedProjects: number;
  totalProjects: number;
  totalRevenue: number;
  pendingAmount: number;
  pendingInvoicesCount: number;
  totalExpenses: number;
  netProfit: number;
  activeClients: number;
  totalClients: number;
  averageProgress: number;
}

interface ProjectionPoint {
  date: string;
  revenue: number;
  costs: number;
  profit: number;
  projectedRevenue: number;
}

interface RiskItem {
  projectId: string;
  projectName: string;
  financialRisk: number;
  scheduleRisk: number;
  qualityRisk: number;
  safetyRisk: number;
  resourceRisk: number;
  overallRisk: number;
  status: 'critical' | 'high' | 'medium' | 'low';
  factors: string[];
}

interface CriticalProject {
  id: string;
  name: string;
  clientName: string;
  riskLevel: string;
  deviation: number;
  deviationPercentage: number;
  progress: number;
  plannedProgress: number;
  daysRemaining: number;
  status: string;
  alerts: string[];
}

interface MarketInsight {
  id: string;
  type: string;
  title: string;
  description: string;
  impact: string;
  recommendation: string;
  confidence: number;
}

type Scenario = 'optimistic' | 'conservative' | 'pessimistic';

const SCENARIO_FACTOR: Record<Scenario, number> = {
  optimistic: 1.15,
  conservative: 1.0,
  pessimistic: 0.85,
};

const fc = (n: number) =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);

const RISK_COLORS: Record<string, string> = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-gray-900',
  low: 'bg-emerald-500 text-white',
};

const RISK_LABELS: Record<string, string> = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Medio',
  low: 'Bajo',
};

export function BIDashboardPage() {
  const { showError } = useToast();
  const [kpis, setKpis] = useState<KPIs | null>(null);
  const [projection, setProjection] = useState<ProjectionPoint[]>([]);
  const [risks, setRisks] = useState<RiskItem[]>([]);
  const [criticalProjects, setCriticalProjects] = useState<CriticalProject[]>([]);
  const [insights, setInsights] = useState<MarketInsight[]>([]);
  const [scenario, setScenario] = useState<Scenario>('conservative');
  const [activeSection, setActiveSection] = useState<'kpis' | 'projection' | 'risks' | 'critical' | 'insights'>('kpis');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [kpisRes, projRes, risksRes, critRes, trendsRes] = await Promise.all([
        authService.fetchWithAuth(`${API_URL}/api/bi/kpis`).catch(() => null),
        authService.fetchWithAuth(`${API_URL}/api/bi/projection?months=6`).catch(() => null),
        authService.fetchWithAuth(`${API_URL}/api/bi/risks`).catch(() => null),
        authService.fetchWithAuth(`${API_URL}/api/bi/critical-projects`).catch(() => null),
        authService.fetchWithAuth(`${API_URL}/api/bi/market-trends`).catch(() => null),
      ]);

      setKpis(kpisRes?.data ?? null);
      setProjection(projRes?.data ?? []);
      setRisks(risksRes?.data ?? []);
      setCriticalProjects(critRes?.data ?? []);
      setInsights(trendsRes?.data ?? []);
    } catch (e: any) {
      setError(e?.message || 'Error al cargar datos de BI');
      showError(e?.message || 'Error al cargar BI');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingState message="Cargando Business Intelligence..." />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const projectionWithScenario = projection.map((p) => ({
    ...p,
    projectedRevenue: Math.round(p.projectedRevenue * SCENARIO_FACTOR[scenario]),
  }));

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Business Intelligence</h1>
          <p className="text-xs text-slate-500">Analítica predictiva del despacho</p>
        </div>
        <button
          onClick={load}
          className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
        >
          🔄 Actualizar
        </button>
      </div>

      <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm overflow-x-auto no-scrollbar">
        {[
          { key: 'kpis', label: 'KPIs' },
          { key: 'projection', label: 'Proyección' },
          { key: 'risks', label: 'Riesgos' },
          { key: 'critical', label: 'Críticos' },
          { key: 'insights', label: 'Tendencias' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveSection(t.key as typeof activeSection)}
            className={`flex-1 min-w-[80px] py-2 rounded-md text-[11px] font-semibold transition-colors ${
              activeSection === t.key ? 'bg-primary text-white' : 'text-slate-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeSection === 'kpis' && kpis && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Ingresos</p>
              <p className="text-base font-bold text-emerald-600">{fc(kpis.totalRevenue)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Gastos</p>
              <p className="text-base font-bold text-red-600">{fc(kpis.totalExpenses)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Utilidad Neta</p>
              <p className="text-base font-bold text-primary">{fc(kpis.netProfit)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Pendiente Cobro</p>
              <p className="text-base font-bold text-amber-600">{fc(kpis.pendingAmount)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Proyectos Activos</p>
              <p className="text-base font-bold">{kpis.activeProjects}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Completados</p>
              <p className="text-base font-bold text-emerald-600">{kpis.completedProjects}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Clientes Activos</p>
              <p className="text-base font-bold">{kpis.activeClients}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-3">
              <p className="text-[10px] text-slate-500">Avance Promedio</p>
              <p className="text-base font-bold">{kpis.averageProgress}%</p>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'projection' && (
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold">Proyección Financiera</h2>
            <select
              value={scenario}
              onChange={(e) => setScenario(e.target.value as Scenario)}
              className="text-xs px-2 py-1 rounded border border-slate-200"
            >
              <option value="optimistic">Optimista</option>
              <option value="conservative">Conservador</option>
              <option value="pessimistic">Pesimista</option>
            </select>
          </div>
          {projectionWithScenario.length === 0 ? (
            <EmptyState icon="📈" title="Sin proyecciones" description="Aún no hay datos suficientes para proyectar." />
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={projectionWithScenario} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => fc(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="revenue" name="Real" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="projectedRevenue" name="Proyectado" stroke="#8b5cf6" strokeDasharray="5 5" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="costs" name="Costos" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                <div className="text-center">
                  <p className="text-[10px] text-slate-500">Ingresos</p>
                  <p className="text-xs font-bold text-emerald-600">
                    {fc(projectionWithScenario.reduce((s, p) => s + p.revenue, 0))}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-500">Costos</p>
                  <p className="text-xs font-bold text-red-600">
                    {fc(projectionWithScenario.reduce((s, p) => s + p.costs, 0))}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-slate-500">Utilidad</p>
                  <p className="text-xs font-bold text-primary">
                    {fc(projectionWithScenario.reduce((s, p) => s + p.profit, 0))}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeSection === 'risks' && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold">Mapa de Calor de Riesgos</h2>
          {risks.length === 0 ? (
            <EmptyState icon="🔥" title="Sin riesgos detectados" />
          ) : (
            risks.map((r) => (
              <div key={r.projectId} className="bg-white rounded-xl shadow-sm p-3">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <p className="text-sm font-semibold text-slate-900 truncate flex-1">
                    {r.projectName}
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${RISK_COLORS[r.status]}`}>
                    {RISK_LABELS[r.status]}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-1 text-center">
                  <div>
                    <p className="text-[9px] text-slate-500">Fin.</p>
                    <p className="text-xs font-bold">{r.financialRisk}%</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500">Cron.</p>
                    <p className="text-xs font-bold">{r.scheduleRisk}%</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500">Cal.</p>
                    <p className="text-xs font-bold">{r.qualityRisk}%</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500">Seg.</p>
                    <p className="text-xs font-bold">{r.safetyRisk}%</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500">Total</p>
                    <p className="text-xs font-bold text-primary">{r.overallRisk}%</p>
                  </div>
                </div>
                {r.factors.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.factors.map((f, i) => (
                      <span key={i} className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded">
                        ⚠️ {f}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeSection === 'critical' && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold">Proyectos Críticos</h2>
          {criticalProjects.length === 0 ? (
            <EmptyState icon="✅" title="Sin proyectos críticos" description="Todos los proyectos están dentro de los parámetros." />
          ) : (
            criticalProjects.map((p) => (
              <div key={p.id} className="bg-white rounded-xl shadow-sm p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{p.clientName}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${RISK_COLORS[p.riskLevel] || 'bg-slate-200 text-slate-700'}`}>
                    {RISK_LABELS[p.riskLevel] || p.riskLevel}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center mt-2">
                  <div>
                    <p className="text-[10px] text-slate-500">Desviación</p>
                    <p className={`text-xs font-bold ${p.deviation >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {p.deviationPercentage > 0 ? '+' : ''}{p.deviationPercentage}%
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500">Progreso</p>
                    <p className="text-xs font-bold">{p.progress}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500">Días</p>
                    <p className={`text-xs font-bold ${p.daysRemaining < 0 ? 'text-red-600' : ''}`}>
                      {p.daysRemaining < 0 ? 'Vencido' : p.daysRemaining}
                    </p>
                  </div>
                </div>
                {p.alerts.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {p.alerts.map((a, i) => (
                      <span key={i} className="text-[10px] bg-red-50 text-red-700 px-2 py-0.5 rounded">
                        🚨 {a}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeSection === 'insights' && (
        <div className="space-y-2">
          <h2 className="text-sm font-bold">Tendencias de Mercado (IA)</h2>
          {insights.length === 0 ? (
            <EmptyState icon="🤖" title="Sin insights" />
          ) : (
            insights.map((ins) => (
              <div key={ins.id} className="bg-white rounded-xl shadow-sm p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-semibold text-slate-900 flex-1">{ins.title}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${
                    ins.impact === 'high' ? 'bg-red-100 text-red-700' :
                    ins.impact === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {ins.impact}
                  </span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">{ins.description}</p>
                <p className="text-[11px] text-primary mt-1.5">💡 {ins.recommendation}</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Confianza: {Math.round(ins.confidence * 100)}%
                </p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}